import { Trans, useTranslation } from 'react-i18next';
import Hyperlink from '@ingka/hyperlink';
import { getCredentialUrl } from '../utils';
import { config } from '../config';
import Message from './Message';
import styled from 'styled-components';
import { useAppContext, getUser } from '../contextProviders/AppContext';
import {
  BOOK_EVENT,
  EVENTS,
  IKEA_BUSINESS_NETWORK_ONLY,
  IKEA_FAMILY_ONLY,
  userAuthStates,
} from '../enums';
import { EventLoyaltyType } from '../types/model';
import { getUserLoyaltyState } from '../services/storeEventApi/utils';

type AlertProps = {
  margin?: string;
  initiator: 'EVENTS' | 'BOOK_EVENT';
  loyaltyEventType?: EventLoyaltyType | undefined;
};

const Wrapper = styled.div<{ margin: string | undefined }>`
  margin: ${({ margin }) => margin || ''};
`;

const LoginAlert = ({ margin, initiator, loyaltyEventType }: AlertProps) => {
  const { t } = useTranslation();
  const { useSelector } = useAppContext();
  const user = useSelector(getUser);
  const registerFamilyUrl = getCredentialUrl(config.joinIkeaFamily);
  const registerRegularUrl = getCredentialUrl(config.joinIkeaRegular);
  const registerBusinessNetworkUrl = getCredentialUrl(
    config.joinIkeaBusinessNetwork
  );
  const loginUrl = getCredentialUrl(config.loginUrl);
  const upgradeUrlFamily = getCredentialUrl(config.upgradeIkeaFamily);
  const upgradeUrlBusinessNetwork = getCredentialUrl(
    config.upgradeIkeaBusinessNetwork
  );

  let registerUrl = registerRegularUrl;
  let upgradeUrl;
  let userAuthState = getUserLoyaltyState(user?.loyalty);

  if (!user) {
    userAuthState = userAuthStates.NOT_LOGGED_IN;
  }

  let message = '';
  if (initiator === BOOK_EVENT) {
    switch (loyaltyEventType) {
      case IKEA_FAMILY_ONLY:
        registerUrl = registerFamilyUrl;
        upgradeUrl = upgradeUrlFamily;
        if (userAuthState !== userAuthStates.LOGGED_IN_AS_FAMILY) {
          message = 'alert.needToBeSignedInFamilyEvent';
        }
        if (userAuthState === userAuthStates.LOGGED_IN_AS_REGULAR) {
          message = 'alert.needToUpgrade';
        }
        break;

      case IKEA_BUSINESS_NETWORK_ONLY:
        registerUrl = registerBusinessNetworkUrl;
        upgradeUrl = upgradeUrlBusinessNetwork;
        if (userAuthState !== userAuthStates.LOGGED_IN_AS_BUSINESS_NETWORK) {
          message = 'alert.needToUpgradeBusinessNetwork';
        }

        if (
          userAuthState === userAuthStates.NOT_LOGGED_IN ||
          userAuthState === userAuthStates.LOGGED_IN_AS_REGULAR ||
          userAuthState === userAuthStates.LOGGED_IN_AS_FAMILY
        ) {
          message = 'alert.needToBeSignedInBusinessNetworkEvent';
        }
        break;

      default:
        if (userAuthState === userAuthStates.NOT_LOGGED_IN) {
          message = 'alert.needToBeSignedInEvent';
        }
    }
  }

  // display alert icon in Events page if we are not logged in.
  if (initiator === EVENTS && userAuthState === userAuthStates.NOT_LOGGED_IN) {
    message = 'alert.needToBeSignedInEvents';
  }

  // don't render any login alert icons if we do not met the conditions above
  if (!message) return null;

  return (
    <Wrapper margin={margin} role="status" aria-live="polite" aria-atomic="true">
      <Message subtle id="login-alert">
        <Trans
          i18nKey={message}
          components={{
            LoggedIn: <Hyperlink url={loginUrl} text={t('alert.loggedIn')} />,
            CreateAccount: (
              <Hyperlink url={registerUrl} text={t('alert.createAccount')} />
            ),
            UpgradeAccount: (
              <Hyperlink url={upgradeUrl} text={t('alert.upgrade')} />
            ),
          }}
        />
      </Message>
    </Wrapper>
  );
};

export default LoginAlert;
